import { slugify } from './menuManager';

/**
 * Logique PURE de l'inscription libre-service (Brique 3, phase A).
 * Aucune dépendance réseau/DOM → testable directement.
 * Couvre : génération du slug/sous-domaine, validation du formulaire,
 * contrôle du code d'invitation. Les écritures Supabase sont dans signupService.ts.
 */

export type Country = 'fr' | 'ch' | 'be';

/** Données saisies par le restaurateur dans le formulaire d'inscription. */
export interface SignupForm {
  restaurantName: string;
  email: string;
  password: string;
  country: Country;
  /** Sous-domaine souhaité (proposé auto depuis le nom, modifiable). */
  slug: string;
  /** Code d'invitation (phase de lancement : accès sur invitation). */
  invitationCode: string;
}

/** Devise déduite du pays (CH → CHF ; FR/BE → EUR). */
export function currencyForCountry(country: Country): 'EUR' | 'CHF' {
  return country === 'ch' ? 'CHF' : 'EUR';
}

/**
 * Domaine racine où le site sera publié.
 * Décision produit (2026-06-17) : **YumLink = domaine unique** pour tous les pays
 * (FR + CH) → `yumlink.io`. Le paramètre `country` est conservé pour compat (devise,
 * routage email) mais ne change plus le domaine affiché au signup.
 * NB : la résolution des anciennes URLs `*.lilandco-services.fr` (legacy) reste
 * reconnue par `resolveTenant` (cf. tenant.ts → ROOT_DOMAINS) — zéro régression.
 */
export function rootDomainForCountry(country: Country): string {
  void country; // domaine unique : le pays ne change plus le domaine (conservé pour compat).
  return 'yumlink.io';
}

/** Adresse complète du futur site (ex. "mon-resto.lilandco-services.fr"). */
export function siteUrlFor(slug: string, country: Country): string {
  return `${slug}.${rootDomainForCountry(country)}`;
}

/**
 * Chemin du site en routing self-service (sans DNS) : "/r/<slug>".
 * Combiné à l'origine courante, donne une URL VIVANTE dès l'inscription
 * (ex. https://lilco-app.pages.dev/r/mon-resto), avant tout sous-domaine.
 */
export function sitePathFor(slug: string): string {
  return `/r/${slug}`;
}

/** Propose un sous-domaine à partir du nom du resto. */
export function suggestSlug(restaurantName: string): string {
  return slugify(restaurantName);
}

/** Un e-mail a-t-il une forme plausible ? (validation simple, non bloquante côté serveur) */
export function isEmailValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** Un slug est-il valide ? (minuscules, chiffres, tirets ; pas de tiret en bord ; 3–40 car.) */
export function isSlugValid(slug: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/.test(slug);
}

export interface SignupValidation {
  ok: boolean;
  /** Erreurs par champ, prêtes à afficher sous chaque saisie. */
  errors: Partial<Record<keyof SignupForm, string>>;
}

/**
 * Valide le formulaire d'inscription.
 * `validCodes` = liste des codes d'invitation acceptés (phase « sur invitation »).
 * Passer `null` désactive le contrôle de code (= ouverture à tous, plus tard).
 */
export function validateSignup(
  form: SignupForm,
  validCodes: string[] | null,
): SignupValidation {
  const errors: SignupValidation['errors'] = {};

  if (form.restaurantName.trim().length < 2) {
    errors.restaurantName = 'Indiquez le nom du restaurant.';
  }
  if (!isEmailValid(form.email)) {
    errors.email = 'E-mail invalide.';
  }
  if (form.password.length < 8) {
    errors.password = 'Mot de passe : 8 caractères minimum.';
  }
  if (form.country !== 'fr' && form.country !== 'ch' && form.country !== 'be') {
    errors.country = 'Choisissez un pays.';
  }
  if (!isSlugValid(form.slug)) {
    errors.slug = 'Adresse invalide (minuscules, chiffres et tirets, 3 à 40 caractères).';
  }
  if (validCodes !== null) {
    const code = form.invitationCode.trim();
    if (!code) errors.invitationCode = "Code d'invitation requis.";
    else if (!validCodes.includes(code)) errors.invitationCode = "Code d'invitation invalide.";
  }

  return { ok: Object.keys(errors).length === 0, errors };
}
